// Settings (spec C14), Users: the first section built (docs/plans/sign-in.md, PR 8).
// Markup from the Settings board and the Table card.
import { useCallback, useMemo, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';
import type { Api, UserRow } from '../auth/api';
import { useUser } from '../auth/user';
import { roleLabels } from '../roles';
import { PageHeader } from '../shell/PageHeader';
import { SearchIcon } from '../shell/icons';
import { Toast } from '../shell/Toast';
import { SettingsNav } from '../settings/SettingsNav';
import { UserEditor } from '../settings/UserEditor';
import {
  lastSignIn,
  managesRole,
  matchesSearch,
  roleFilters,
  showing,
  statusClass,
  statusLabel,
  usersCopy as c,
} from '../settings/user-lines';

export type SettingsLoaderData = { users: UserRow[] };

// Name, Email, Role, Initials, Status, Open renewals, Last sign-in.
const columns =
  'minmax(0, 1.1fr) minmax(0, 1.9fr) minmax(0, 1fr) minmax(0, 0.5fr) minmax(0, 1fr) minmax(0, 0.9fr) minmax(0, 0.9fr)';

type Editing = { user: UserRow | null } | null;

export function SettingsPage({ api }: { api: Api }) {
  const { users } = useLoaderData() as SettingsLoaderData;
  const actor = useUser();
  const revalidator = useRevalidator();
  const [filter, setFilter] = useState<(typeof roleFilters)[number]['key']>('all');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<Editing>(null);
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);

  const searched = useMemo(() => users.filter((u) => matchesSearch(u, query)), [users, query]);
  const rows = searched.filter((u) => filter === 'all' || u.role === filter);
  const close = useCallback(() => setEditing(null), []);
  const done = useCallback(
    (text: string) => {
      setEditing(null);
      setToast({ id: Date.now(), text });
      revalidator.revalidate();
    },
    [revalidator],
  );

  return (
    <>
      <PageHeader title="Settings" subtitle="Changes apply to everyone in the department" />
      <div className="setwrap">
        <SettingsNav current="/settings" />
        <div className="setcol">
          <div className="row g12 between">
            <h2 className="dtitle">{c.title}</h2>
            <button
              type="button"
              className="btn btn-primary"
              aria-haspopup="dialog"
              onClick={() => setEditing({ user: null })}
            >
              {c.addUser}
            </button>
          </div>
          <div className="ltb">
            <div className="row g6 wrap" role="group" aria-label="Filter by role">
              {roleFilters.map((f) => {
                const n = searched.filter((u) => f.key === 'all' || u.role === f.key).length;
                return (
                  <button
                    key={f.key}
                    type="button"
                    className={filter === f.key ? 'chip on' : 'chip'}
                    aria-pressed={filter === f.key}
                    onClick={() => setFilter(f.key)}
                  >
                    {f.label}
                    <span className="n">{n}</span>
                  </button>
                );
              })}
            </div>
            <div className="ltb-r">
              <div className="searchbox">
                <SearchIcon />
                <label htmlFor="us-q" className="sr">
                  {c.search}
                </label>
                <input
                  id="us-q"
                  className="input"
                  type="search"
                  placeholder={c.search}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="hdr" style={{ gridTemplateColumns: columns }}>
            {['Name', 'Email', 'Role', 'Initials', 'Status', 'Open renewals', 'Last sign-in'].map(
              (h) => (
                <div key={h} className="th t12 muted w5">
                  <span className="ell">{h}</span>
                </div>
              ),
            )}
          </div>
          {rows.length === 0 ? (
            <div className="gcard lempty">
              <span>{query.trim() ? c.noMatch : c.noneWithFilter}</span>
              <button
                type="button"
                className="linkbtn"
                onClick={() => {
                  setQuery('');
                  setFilter('all');
                }}
              >
                {c.clearSearch}
              </button>
            </div>
          ) : (
            <div className="gcard">
              {rows.map((u) => (
                <div
                  key={u.id}
                  className="trow"
                  style={{ display: 'grid', gridTemplateColumns: columns, alignItems: 'center' }}
                >
                  <div className="td vis inscell">
                    {u.id === actor.id || managesRole(actor.role, u.role) ? (
                      <button
                        type="button"
                        className="ins"
                        aria-haspopup="dialog"
                        aria-label={`Edit ${u.name}`}
                        onClick={() => setEditing({ user: u })}
                      >
                        {u.name}
                      </button>
                    ) : (
                      <span className="ell ink">{u.name}</span>
                    )}
                  </div>
                  <div className="td text2">
                    <span className="ell">{u.email}</span>
                  </div>
                  <div className="td vis">
                    <span className="tag t-grey">{roleLabels[u.role]}</span>
                  </div>
                  <div className="td mono text2">
                    <span className="ell">{u.initials}</span>
                  </div>
                  <div className="td vis">
                    <span className={statusClass(u.status)}>{statusLabel(u.status)}</span>
                  </div>
                  {/* Renewals arrive with the AMS360 sync; until then nobody has any open. */}
                  <div className="td mono ink">
                    <span className="ell">{u.role === 'admin' ? '-' : '0'}</span>
                  </div>
                  <div className="td text2">
                    <span className="ell">{lastSignIn(u.lastSignInAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="lfoot">{showing(rows.length, users.length)}</div>
          <p className="esub">{c.note}</p>
        </div>
      </div>
      {editing && (
        <UserEditor api={api} actor={actor} user={editing.user} onClose={close} onDone={done} />
      )}
      {toast && <Toast key={toast.id} text={toast.text} />}
    </>
  );
}
